/**
 * rondo-web-forms
 *
 * Rondo Web Forms
 *
 * @author Jiri Hybek <jiri@hybek.cz>
 * @license See LICENSE file distributed with this source code.
 */

import { FormElement } from "../FormElement";
import { IFormElementConfig } from "../IFormElementConfig";
import { FormModel } from "../FormModel";
import { FORM_EL_TYPE } from "../FormElementTypes";
import { IFormElementInlineConfig } from "./IFormElementInlineConfig";
import { IFormExpressionFn } from "../IFormExpressionFn";

/**
 * Element config
 */
export interface IFormFieldLabelConfig extends IFormElementConfig, IFormElementInlineConfig {
	label?: string|IFormExpressionFn<FormFieldLabel, string>;
	value?: string|IFormExpressionFn<FormFieldLabel, string>;
	labelAlign?: "left"|"center"|"right";
	align?: "left"|"center"|"right";
	verticalAlign?: "top"|"center"|"bottom";
	style?: "default"|"summary";
	suffix?: string;
}

/**
 * Field Label class
 */
export class FormFieldLabel extends FormElement<IFormFieldLabelConfig> {

	private labelValue: string;
	private value: string;

	/**
	 * Element constructor
	 *
	 * @param model Model instance
	 * @param parent Parent element
	 * @param config Element configuration
	 */
	public constructor(
		model: FormModel,
		parent: FormElement<any>,
		config: IFormFieldLabelConfig
	) {

		super(model, parent, FORM_EL_TYPE.FIELD_LABEL, config);

	}

	/**
	 * Updates the element
	 *
	 * @param resetState If to reset state
	 */
	public update(resetState: boolean = false) {

		// Label
		if (typeof this.config.label === "function") {

			this.labelValue = "";

			this.model._addExpression(() => {

				this.labelValue = (this.config.label as IFormExpressionFn)(this.model, this);
				this.emit("update");

			});

		} else {

			this.labelValue = this.config.label;

		}

		// Value
		if (typeof this.config.value === "function") {

			this.value = "";

			this.model._addExpression(() => {

				this.value = (this.config.value as IFormExpressionFn)(this.model, this);
				this.emit("update");

			});

		} else {

			this.value = this.config.value;

		}

	}

	/**
	 * Returns element state
	 */
	public getState() {

		return {
			...super.getState(),
			labelValue: this.labelValue,
			value: this.value
		}

	}

}

/**
 * Declares field label
 *
 * @param config Config
 */
export function FieldLabel(
	config: IFormFieldLabelConfig
) {

	return (model, parent) => new FormFieldLabel(model, parent, config);

}
